/**
 * GN Header Info-Slider v1 | Shopify Horizon
 * - Keine Abhängigkeit von Horizon-JavaScript
 * - Theme-Editor-kompatibles Custom Element
 * - Mobile Auto-Slide/Swipe/Dots/Arrows
 * - Desktop: keine versteckten Gruppen
 */
if (!customElements.get('gn-header-info-slider')) {
  class GnHeaderInfoSlider extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      this._track = this.querySelector('[data-gnh-track]');
      if (!this._track) return;

      this._slides = Array.from(this._track.children).filter((element) => element.classList.contains('group-block'));
      this._mobile = window.matchMedia('(max-width: 749px)');
      this._reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this._index = 0;
      this._pausedByPointer = false;
      this._pausedByFocus = false;
      this._pausedByEditor = false;
      this._visible = true;
      this._timer = null;
      this._interval = Math.max(2000, Number(this.dataset.interval) || 4000);
      this._autoplayEnabled = this.dataset.autoplay === 'true';
      this._swipeEnabled = this.dataset.swipe === 'true';
      this._pauseOnHover = this.dataset.pauseHover === 'true';
      this._designMode = this.dataset.designMode === 'true';

      this.dataset.arrows = this.querySelector('[data-gnh-prev]') ? 'true' : 'false';
      this._prev = this.querySelector('[data-gnh-prev]');
      this._next = this.querySelector('[data-gnh-next]');
      this._dotsContainer = this.querySelector('[data-gnh-dots]');

      this._handleMedia = () => {
        this._render();
        this._restart();
      };
      this._handleVisibility = () => this._restart();
      this._handleEnter = (event) => { if (this._pauseOnHover && event.pointerType === 'mouse') { this._pausedByPointer = true; this._restart(); } };
      this._handleLeave = (event) => { if (event.pointerType !== 'mouse') return; this._pausedByPointer = false; this._restart(); };
      this._handleFocusIn = () => { this._pausedByFocus = true; this._restart(); };
      this._handleFocusOut = (event) => {
        if (!this.contains(event.relatedTarget)) {
          this._pausedByFocus = false;
          this._restart();
        }
      };
      this._handleSelect = (event) => {
        if (!this._designMode || !this.contains(event.target)) return;
        const group = event.target.closest('.group-block');
        if (!group) return;
        const directGroup = group.closest('.gnh__track > .group-block');
        const index = this._slides.indexOf(directGroup || group);
        if (index !== -1) this._go(index);
        this._pausedByEditor = true;
        this._restart();
      };
      this._handleDeselect = () => {
        this._pausedByEditor = false;
        this._restart();
      };
      this._handleTouchStart = (event) => {
        if (!this._swipeEnabled || !this._mobile.matches || event.touches.length !== 1) return;
        this._touchStart = { x: event.touches[0].clientX, y: event.touches[0].clientY };
      };
      this._handleTouchEnd = (event) => {
        if (!this._touchStart || !this._mobile.matches || event.changedTouches.length === 0) return;
        const dx = event.changedTouches[0].clientX - this._touchStart.x;
        const dy = event.changedTouches[0].clientY - this._touchStart.y;
        this._touchStart = null;
        if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.4) {
          this._go(this._index + (dx < 0 ? 1 : -1));
          this._restart();
        }
      };

      this._mobile.addEventListener('change', this._handleMedia);
      this._reducedMotion.addEventListener('change', this._handleMedia);
      document.addEventListener('visibilitychange', this._handleVisibility);
      this.addEventListener('pointerenter', this._handleEnter);
      this.addEventListener('pointerleave', this._handleLeave);
      this.addEventListener('focusin', this._handleFocusIn);
      this.addEventListener('focusout', this._handleFocusOut);
      this.addEventListener('shopify:block:select', this._handleSelect);
      this.addEventListener('shopify:block:deselect', this._handleDeselect);
      this.addEventListener('touchstart', this._handleTouchStart, { passive: true });
      this.addEventListener('touchend', this._handleTouchEnd, { passive: true });

      this._handlePrev = () => { this._go(this._index - 1); this._restart(); };
      this._handleNext = () => { this._go(this._index + 1); this._restart(); };
      this._prev?.addEventListener('click', this._handlePrev);
      this._next?.addEventListener('click', this._handleNext);

      if ('IntersectionObserver' in window) {
        this._observer = new IntersectionObserver((entries) => {
          this._visible = entries.some((entry) => entry.isIntersecting);
          this._restart();
        }, { threshold: 0.05 });
        this._observer.observe(this);
      }

      const hint = this.querySelector('[data-gnh-hint]');
      if (hint && this._slides.length === 0) hint.hidden = false;

      this._makeDots();
      this._render();
      this._restart();
      this.dataset.ready = 'true';
    }

    disconnectedCallback() {
      this._stop();
      this._observer?.disconnect();
      this._mobile?.removeEventListener('change', this._handleMedia);
      this._reducedMotion?.removeEventListener('change', this._handleMedia);
      document.removeEventListener('visibilitychange', this._handleVisibility);
      this.removeEventListener('pointerenter', this._handleEnter);
      this.removeEventListener('pointerleave', this._handleLeave);
      this.removeEventListener('focusin', this._handleFocusIn);
      this.removeEventListener('focusout', this._handleFocusOut);
      this.removeEventListener('shopify:block:select', this._handleSelect);
      this.removeEventListener('shopify:block:deselect', this._handleDeselect);
      this.removeEventListener('touchstart', this._handleTouchStart);
      this.removeEventListener('touchend', this._handleTouchEnd);
      this._prev?.removeEventListener('click', this._handlePrev);
      this._next?.removeEventListener('click', this._handleNext);
      this._initialized = false;
    }

    _makeDots() {
      if (!this._dotsContainer) return;
      this._dotsContainer.replaceChildren();
      if (this._slides.length <= 1) return;
      this._slides.forEach((_, index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'gnh__dot';
        dot.setAttribute('aria-label', `Hinweis ${index + 1} von ${this._slides.length} anzeigen`);
        dot.addEventListener('click', () => { this._go(index); this._restart(); });
        this._dotsContainer.append(dot);
      });
    }

    _go(index) {
      if (!this._slides.length) return;
      this._index = ((index % this._slides.length) + this._slides.length) % this._slides.length;
      this._render();
    }

    _render() {
      const isMobile = this._mobile?.matches;
      this.style.setProperty('--gnh-translate', `${-100 * this._index}%`);
      this._slides.forEach((slide, index) => {
        const active = index === this._index;
        slide.classList.toggle('gnh--active', active);
        if (isMobile && !active) {
          slide.setAttribute('aria-hidden', 'true');
          slide.inert = true;
        } else {
          slide.removeAttribute('aria-hidden');
          slide.inert = false;
        }
      });
      this._dotsContainer?.querySelectorAll('button').forEach((dot, index) => {
        dot.setAttribute('aria-current', index === this._index ? 'true' : 'false');
      });
      [this._prev, this._next].forEach((button) => {
        if (button) button.hidden = !isMobile || this._slides.length <= 1;
      });
    }

    _stop() {
      if (this._timer) window.clearInterval(this._timer);
      this._timer = null;
    }

    _restart() {
      this._stop();
      if (!this._autoplayEnabled || !this._mobile?.matches || this._slides.length <= 1) return;
      if (document.hidden || this._reducedMotion.matches || !this._visible) return;
      if (this._pausedByPointer || this._pausedByFocus || this._pausedByEditor) return;
      this._timer = window.setInterval(() => this._go(this._index + 1), this._interval);
    }
  }

  customElements.define('gn-header-info-slider', GnHeaderInfoSlider);
}
